/**
 * Reading an `.xlsx` workbook, to the extent a report needs it.
 *
 * A workbook is a zip of XML parts, and only four of them matter here: the
 * workbook (sheet names and their order), its relationships (which part each
 * sheet is), the shared string table (nearly every text cell is an index into
 * it) and the style table. The style table is not optional, and this is the one
 * surprise about the format: **a date in Excel is a number, and the only thing
 * that says otherwise is its number format**. A reader that skips styles turns
 * every date in the file into 46000-something, and the user's first impression
 * of the product is a column of five-digit integers where their invoice dates
 * used to be.
 *
 * Read with `fflate` for the inflating, which is small and works in a browser
 * as well as in Node — the whole point of parsing here is that the file is read
 * on the user's own machine and never uploaded, so this has to run in a tab.
 *
 * Deliberately not supported: formulas (the cached value is read instead, which
 * is what the user sees in Excel), `.xls`, charts, images, merged-cell geometry
 * and anything to do with appearance beyond whether a number is a date.
 */
import { unzipSync, type UnzipFileInfo } from "fflate";

export interface Sheet {
  name: string;
  /** Rows of cell values: strings, numbers, booleans, Dates, or null for blanks. */
  rows: Array<Array<string | number | boolean | Date | null>>;
  truncated: boolean;
}

export interface XlsxLimits {
  maxRows: number;
  maxColumns: number;
  /**
   * Total uncompressed bytes this reader will inflate.
   *
   * A zip declares how big each entry expands to, which is what makes a cap
   * enforceable before anything is decompressed rather than after. A workbook is
   * mostly XML and compresses ten to one or better, so a few hundred megabytes
   * of declared size behind a small upload is not a big spreadsheet — it is a
   * zip bomb, and the honest answer is to refuse it.
   */
  maxUncompressedBytes: number;
  maxEntries: number;
}

export const XLSX_LIMITS: XlsxLimits = {
  maxRows: 50_000,
  maxColumns: 120,
  maxUncompressedBytes: 256 * 1024 * 1024,
  maxEntries: 512
};

export class XlsxError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "XlsxError";
  }
}

/** Parts worth inflating. Everything else in a workbook is appearance or metadata. */
const WANTED = /^xl\/(workbook\.xml|sharedStrings\.xml|styles\.xml|_rels\/workbook\.xml\.rels|worksheets\/[^/]+\.xml)$/;

export function readWorkbook(bytes: Uint8Array, limits: Partial<XlsxLimits> = {}): Sheet[] {
  const lim = { ...XLSX_LIMITS, ...limits };
  let declared = 0;
  let entries = 0;

  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes, {
      filter: (file: UnzipFileInfo) => {
        if (++entries > lim.maxEntries) {
          throw new XlsxError("This workbook contains an unusual number of parts and has not been opened.");
        }
        if (!WANTED.test(file.name)) return false;
        declared += file.originalSize;
        if (declared > lim.maxUncompressedBytes) {
          throw new XlsxError("This file expands to far more than its size suggests and has not been opened.");
        }
        return true;
      }
    });
  } catch (e) {
    if (e instanceof XlsxError) throw e;
    throw new XlsxError("We couldn't read this as an Excel workbook. If you saved it as .xls, save it again as .xlsx.");
  }

  const text = (name: string): string | null => (files[name] ? new TextDecoder().decode(files[name]!) : null);

  const workbook = text("xl/workbook.xml");
  if (!workbook) throw new XlsxError("We couldn't read this as an Excel workbook. If you saved it as .xls, save it again as .xlsx.");

  const strings = sharedStrings(text("xl/sharedStrings.xml"));
  const dateStyles = dateFormattedStyles(text("xl/styles.xml"));
  const targets = relationships(text("xl/_rels/workbook.xml.rels"));

  const sheets: Sheet[] = [];
  for (const { name, rid } of sheetList(workbook)) {
    const target = rid ? targets.get(rid) : undefined;
    const part = target ? `xl/${target.replace(/^\/?xl\//, "").replace(/^\//, "")}` : undefined;
    const xml = (part && text(part)) || null;
    if (!xml) continue;
    sheets.push({ name, ...readSheet(xml, strings, dateStyles, lim) });
  }
  return sheets;
}

/* ------------------------------- the parts ------------------------------- */

function sheetList(xml: string): Array<{ name: string; rid?: string }> {
  const out: Array<{ name: string; rid?: string }> = [];
  for (const m of xml.matchAll(/<sheet\b([^>]*)\/?>/g)) {
    const attrs = m[1] ?? "";
    const name = attr(attrs, "name");
    if (name === null) continue;
    const rid = attr(attrs, "r:id") ?? attr(attrs, "id") ?? undefined;
    out.push(rid ? { name: decode(name), rid } : { name: decode(name) });
  }
  return out;
}

function relationships(xml: string | null): Map<string, string> {
  const map = new Map<string, string>();
  if (!xml) return map;
  for (const m of xml.matchAll(/<Relationship\b([^>]*)\/?>/g)) {
    const attrs = m[1] ?? "";
    const id = attr(attrs, "Id");
    const target = attr(attrs, "Target");
    if (id && target) map.set(id, decode(target));
  }
  return map;
}

/**
 * The shared string table.
 *
 * A cell's text is usually an index into this rather than the text itself, and
 * a string may be split across several `<r>` runs when part of it was styled
 * differently. The runs are concatenated: a report wants the sentence, not the
 * formatting that was applied to three words of it.
 */
function sharedStrings(xml: string | null): string[] {
  if (!xml) return [];
  const out: string[] = [];
  for (const si of xml.matchAll(/<si>([\s\S]*?)<\/si>/g)) {
    const body = si[1] ?? "";
    let value = "";
    for (const t of body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)) value += decode(t[1] ?? "");
    out.push(value);
  }
  return out;
}

/**
 * Which style indexes mean "this number is a date".
 *
 * Two sources. The built-in format ids are fixed by the specification —
 * 14 to 22 are dates and times, 45 to 47 are durations — and a custom format is
 * a date if its code contains a day, month or year token outside a literal
 * string. The `[^"]*` dance matters: `0.00"m"` is a number with the letter m in
 * a quoted suffix, not a month, and a reader that pattern-matches the whole
 * code turns a price into 1900.
 */
function dateFormattedStyles(xml: string | null): Set<number> {
  const dates = new Set<number>();
  if (!xml) return dates;

  const custom = new Map<number, string>();
  for (const m of xml.matchAll(/<numFmt\b([^>]*)\/?>/g)) {
    const attrs = m[1] ?? "";
    const id = Number(attr(attrs, "numFmtId"));
    const code = attr(attrs, "formatCode");
    if (Number.isFinite(id) && code !== null) custom.set(id, decode(code));
  }

  const isDateFormat = (id: number): boolean => {
    if ((id >= 14 && id <= 22) || (id >= 45 && id <= 47)) return true;
    const code = custom.get(id);
    if (!code) return false;
    const bare = code.replace(/"[^"]*"/g, "").replace(/\[[^\]]*\]/g, "");
    return /[dmyhs]/i.test(bare) && /[dmy]/i.test(bare);
  };

  const cellXfs = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(xml)?.[1] ?? "";
  let index = 0;
  for (const xf of cellXfs.matchAll(/<xf\b([^>]*?)(?:\/>|>)/g)) {
    const id = Number(attr(xf[1] ?? "", "numFmtId") ?? "0");
    if (Number.isFinite(id) && isDateFormat(id)) dates.add(index);
    index++;
  }
  return dates;
}

function readSheet(xml: string, strings: string[], dateStyles: Set<number>, lim: XlsxLimits): Omit<Sheet, "name"> {
  const rows: Sheet["rows"] = [];
  let truncated = false;
  // Rows with a value in them, which is what the cap counts: Excel saves formatted
  // empty rows under the data, and a file exactly at the limit with a few of those
  // below it was told it had been cut short.
  let counted = 0;

  for (const rowMatch of xml.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
    const cells: Sheet["rows"][number] = [];
    for (const cellMatch of (rowMatch[2] ?? "").matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = cellMatch[1] ?? "";
      const body = cellMatch[2] ?? "";
      // The column comes from the reference, not from the order cells appear
      // in: Excel omits empty cells entirely, so `A1` then `D1` means two
      // blanks between them and a reader that pushes in order shifts the row.
      const ref = attr(attrs, "r");
      const column = ref ? columnIndex(ref) : cells.length;
      if (column >= lim.maxColumns) continue;
      while (cells.length < column) cells.push(null);
      cells[column] = cellValue(attrs, body, strings, dateStyles);
    }
    const filled = cells.some((v) => v !== null && v !== "");
    if (counted >= lim.maxRows) {
      if (!filled) continue;
      truncated = true;
      break;
    }
    rows.push(cells);
    if (filled) counted++;
  }

  // Trailing rows that are entirely empty are not data, however many of them a
  // spreadsheet has been saved with.
  while (rows.length && rows[rows.length - 1]!.every((v) => v === null || v === "")) rows.pop();
  return { rows, truncated };
}

function cellValue(attrs: string, body: string, strings: string[], dateStyles: Set<number>): string | number | boolean | Date | null {
  const type = attr(attrs, "t") ?? "n";
  if (type === "inlineStr") {
    let value = "";
    for (const t of body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)) value += decode(t[1] ?? "");
    return value === "" ? null : value;
  }
  const raw = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
  if (raw === undefined) return null;
  const value = decode(raw);

  switch (type) {
    case "s": {
      const text = strings[Number(value)];
      return text === undefined || text === "" ? null : text;
    }
    case "str":
      return value === "" ? null : value;
    case "b":
      return value === "1";
    case "e":
      // A formula error — #DIV/0!, #N/A. Empty is the honest reading: the
      // spreadsheet does not have a value here either.
      return null;
    default: {
      const n = Number(value);
      if (!Number.isFinite(n)) return value === "" ? null : value;
      const style = Number(attr(attrs, "s") ?? "-1");
      return dateStyles.has(style) ? serialToDate(n) : n;
    }
  }
}

/**
 * An Excel date serial as a date.
 *
 * The epoch is 30 December 1899 rather than the 1 January 1900 the format
 * claims, because Excel believes 1900 was a leap year and every date after
 * February 1900 is therefore one too high. Shifting the epoch back a day is the
 * standard correction and is exact for every date a business document contains.
 *
 * Below 1 the value is a time of day with no date, which is not something a
 * report can show; it comes back as a date on the epoch, and the type vote
 * discards the column if that is all it holds.
 */
export function serialToDate(serial: number): Date {
  const EPOCH = Date.UTC(1899, 11, 30);
  const days = Math.floor(serial);
  const fraction = serial - days;
  // Rounded to the second: floating point turns 0.5 of a day into 11:59:59.998
  // often enough to matter in a column of timestamps.
  const ms = Math.round(fraction * 86_400) * 1000;
  return new Date(EPOCH + days * 86_400_000 + ms);
}

/** `BC12` → 54. Base-26 on the letters, ignoring the row. */
export function columnIndex(ref: string): number {
  let n = 0;
  for (let i = 0; i < ref.length; i++) {
    const code = ref.charCodeAt(i);
    if (code < 65 || code > 90) break;
    n = n * 26 + (code - 64);
  }
  return Math.max(0, n - 1);
}

function attr(attrs: string, name: string): string | null {
  const m = new RegExp(`\\b${name.replace(":", "\\:")}\\s*=\\s*"([^"]*)"`).exec(attrs);
  return m ? (m[1] ?? "") : null;
}

function decode(xml: string): string {
  if (!xml.includes("&")) return xml;
  return xml
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(Number(dec)))
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    // Ampersand last, or `&amp;lt;` decodes twice and becomes a tag.
    .replace(/&amp;/g, "&");
}
